import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { Sparkles, Check, ShieldCheck, IndianRupee, ArrowRight, Calendar, FileStack, BookOpenText, BarChart3, Flame, Users, Ticket } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { openCheckout } from "../lib/razorpay";
import { PRICING_FALLBACK, formatPrice, withPricing } from "../lib/pricing";

export default function Premium() {
  const nav = useNavigate();
  const { user, isAuthed, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [config, setConfig] = useState(PRICING_FALLBACK);
  const [sub, setSub] = useState(null);
  const [premiumCount, setPremiumCount] = useState(null);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);       // { valid, effective_inr, ... }
  const [couponBusy, setCouponBusy] = useState(false);

  useEffect(() => {
    api.subConfig().then((c) => setConfig(withPricing(c))).catch(() => {});
    api.premiumCount().then(setPremiumCount).catch(() => {});
    if (isAuthed) api.subMe().then(setSub).catch(() => {});
  }, [isAuthed]);

  const applyCoupon = async () => {
    const c = couponInput.trim().toUpperCase();
    if (!c) return;
    setCouponBusy(true);
    try {
      const r = await api.validateCoupon(c);
      if (!r?.valid) { setCoupon(null); toast.error("Invalid or expired coupon"); return; }
      setCoupon(r);
      toast.success(`Coupon applied — pay ₹${formatPrice(r.effective_inr)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Coupon failed");
    } finally { setCouponBusy(false); }
  };
  const clearCoupon = () => { setCoupon(null); setCouponInput(""); };

  const startCheckout = async () => {
    if (!isAuthed) {
      toast.info("Please sign in first to buy Premium.");
      return nav("/login");
    }
    if (sub?.is_premium) {
      toast.info("You already have an active Premium subscription.");
      return nav("/daily-entry");
    }
    setBusy(true);
    try {
      const order = await api.createSubOrder(coupon?.code);
      await openCheckout({
        order, user, plan: order.plan,
        onSuccess: async (resp) => {
          // Small verifying state so users don't panic during signature check
          const verifyingId = toast.loading("Verifying your payment…", { duration: 20000 });
          try {
            const r = await api.verifySubPayment({
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            // Refresh the authenticated user's state so /daily-entry sees Premium immediately
            await refresh();
            try { await api.subMe().then(setSub); } catch (_) { /* non-blocking */ }
            toast.dismiss(verifyingId);
            // 🎉 Confetti burst — celebrate the moment
            try {
              const fire = (opts) => confetti({
                particleCount: 80, spread: 70, ticks: 200, origin: { y: 0.65 },
                colors: ["#059669", "#f59e0b", "#0ea5e9", "#ef4444", "#a855f7"],
                ...opts,
              });
              fire({ angle: 60, origin: { x: 0, y: 0.7 } });
              fire({ angle: 120, origin: { x: 1, y: 0.7 } });
              setTimeout(() => fire({ particleCount: 120, spread: 100, origin: { y: 0.6 } }), 250);
            } catch (_) { /* confetti is a nice-to-have */ }
            toast.success("🎉 Payment Successful! Premium Activated.", {
              description: "Daily Entry is now unlocked.",
              duration: 4000,
            });
            // Instant client-side redirect — no page refresh. `welcome=premium`
            // triggers the one-time welcome card + 3-step onboarding tour.
            nav("/daily-entry?welcome=premium", { replace: true });
            return r;
          } catch (e) {
            toast.dismiss(verifyingId);
            toast.error(e?.response?.data?.detail || "Verification failed. Please contact support with your Payment ID.");
          }
        },
      });
    } catch (e) {
      if (e?.message === "Payment cancelled") {
        toast.info("Payment cancelled.");
      } else {
        toast.error(e?.response?.data?.detail || e?.message || "Payment failed");
      }
    } finally { setBusy(false); }
  };

  const price = config.price_inr;
  const originalPrice = config.original_price_inr;
  const savings = config.savings_inr;
  const offerLabel = config.offer_label;
  const effectivePrice = coupon?.valid ? coupon.effective_inr : price;

  const perks = [
    { icon: <Calendar size={20}/>, title: "Save Daily Entries", desc: "One tap to save each day's meal + attendance." },
    { icon: <BookOpenText size={20}/>, title: "Daily History", desc: "See every day you've served, per school." },
    { icon: <BarChart3 size={20}/>, title: "Reports", desc: "Daily / Weekly / Monthly / Annual — with PDF & Excel export." },
    { icon: <FileStack size={20}/>, title: "Bulk PDF", desc: "One PDF for a whole week or month, inspection-ready." },
    { icon: <ShieldCheck size={20}/>, title: "Private register", desc: "Your entries live under your account, not by UDISE alone." },
    { icon: <Sparkles size={20}/>, title: "Priority updates", desc: "New menus, category norms and Odisha rate changes first." },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="premium-page">
      <div className="text-center">
        <div className="chip chip-saffron inline-flex items-center gap-1"><Flame size={14}/> {offerLabel} · ₹{formatPrice(price)} / year</div>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold text-slate-900">Turn PM Poshan into a proper register</h1>
        <p className="mt-2 text-slate-600 max-w-2xl mx-auto">The calculator, Ladoo tool and Poshan Panji-by-UDISE will always be free. Premium unlocks personal record-keeping, month-end reports and Bulk PDF for the inspection file.</p>
        {premiumCount?.count > 0 && (
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-900 px-4 py-1.5 text-sm font-bold shadow-sm" data-testid="premium-live-counter">
            <Users size={14}/> {formatPrice(premiumCount.count)} teachers on Premium
          </div>
        )}
      </div>

      {/* Price card */}
      <div className="mt-8 grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-display font-bold text-lg text-slate-900">What's included</h2>
          <ul className="mt-4 grid sm:grid-cols-2 gap-x-4 gap-y-3">
            {perks.map((p) => (
              <li key={p.title} className="flex items-start gap-3">
                <span className="h-8 w-8 rounded-lg bg-emerald-50 text-emerald-700 grid place-items-center shrink-0">{p.icon}</span>
                <div>
                  <div className="font-semibold text-slate-900 flex items-center gap-1"><Check size={14} className="text-emerald-600"/> {p.title}</div>
                  <div className="text-sm text-slate-600">{p.desc}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative rounded-2xl border-2 border-emerald-700 bg-gradient-to-br from-emerald-50 to-amber-50 p-6 text-center flex flex-col shadow-sm" data-testid="premium-price-card">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-orange-500 text-white text-[10px] uppercase tracking-widest font-extrabold px-3 py-1 shadow">
            <span className="inline-flex items-center gap-1"><Flame size={11}/> {offerLabel}</span>
          </div>
          <div className="chip chip-green mx-auto mt-2"><Sparkles size={12}/> Premium Annual Plan</div>
          <div className="mt-3 flex items-end justify-center gap-2" data-testid="premium-price-value">
            <span className="text-slate-400 text-2xl font-semibold line-through decoration-2 decoration-red-400" data-testid="premium-original-price">₹{formatPrice(originalPrice)}</span>
            <span className="font-display font-extrabold text-emerald-800 text-5xl inline-flex items-center leading-none"><IndianRupee size={30} strokeWidth={2.5}/>{formatPrice(price)}</span>
          </div>
          <div className="mt-1 text-sm font-semibold text-slate-700">/ year · one-time payment</div>
          <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-300 text-amber-900 px-3 py-1 text-xs font-bold self-center" data-testid="premium-savings">
            💰 Save ₹{formatPrice(savings)}
          </div>
          <div className="mt-1 text-[11px] text-slate-500">Regular price ₹{formatPrice(originalPrice)} · No auto-renewal</div>

          {sub?.is_premium ? (
            <div className="mt-5 rounded-lg bg-emerald-100 border border-emerald-200 p-3 text-sm">
              <div className="font-semibold text-emerald-900 flex items-center gap-1"><Check size={14}/> You're Premium!</div>
              <div className="text-emerald-800/80 mt-1">Valid until {new Date(sub.subscription.expiry_date).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}.</div>
            </div>
          ) : (
            <>
              {/* Coupon */}
              <div className="mt-5 rounded-xl border border-emerald-200 bg-white p-3" data-testid="premium-coupon-block">
                {coupon?.valid ? (
                  <div className="flex items-center gap-2">
                    <Ticket size={16} className="text-emerald-700"/>
                    <div className="flex-1 text-sm">
                      <div className="font-bold text-emerald-800">{coupon.code} applied</div>
                      <div className="text-xs text-slate-600">Pay <b>₹{formatPrice(effectivePrice)}</b> instead of ₹{formatPrice(price)}</div>
                    </div>
                    <button onClick={clearCoupon} className="text-xs text-slate-500 hover:text-slate-800 font-semibold" data-testid="premium-coupon-clear">Remove</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Ticket size={16} className="text-emerald-700 shrink-0"/>
                    <Input value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} placeholder="Have a coupon? e.g. TEACHER100" className="h-10 uppercase text-sm" data-testid="premium-coupon-input"/>
                    <Button onClick={applyCoupon} disabled={couponBusy || !couponInput.trim()} variant="outline" className="h-10 whitespace-nowrap" data-testid="premium-coupon-apply">Apply</Button>
                  </div>
                )}
              </div>
              <Button onClick={startCheckout} disabled={busy || !config?.enabled} className="mt-3 h-12 bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-base rounded-xl" data-testid="premium-buy-btn">
                {busy ? "Opening checkout…" : (isAuthed ? `Activate Premium – ₹${formatPrice(effectivePrice)}/year` : "Sign in to buy")}
                <ArrowRight size={16}/>
              </Button>
            </>
          )}
          <p className="mt-2 text-[11px] text-slate-500">Secure payment · Razorpay · UPI, cards, netbanking, wallets</p>
          {!config?.enabled && (
            <p className="mt-2 text-[11px] text-amber-700">Payments temporarily unavailable — please try again later.</p>
          )}
        </div>
      </div>

      {/* Free tier reminder */}
      <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-700" data-testid="premium-free-note">
        <b>Free tier stays free forever:</b> anonymous Calculator, Ladoo Calculator and Poshan Panji-by-UDISE. Try them any time from the main menu — no account needed.
      </div>
    </div>
  );
}
